import React, { useMemo, useState } from 'react';
import {
  DndContext, DragEndEvent, DragStartEvent,
  MouseSensor, TouchSensor, useSensor, useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { KanbanCard } from './KanbanCard';
import { SortableItem } from './SortableItem';
import { useToastApi } from '../../utils/toastApi';

type TaleLite = {
  id: string;
  displayTitle: string;
  verse: string | null;
  status: string;
  createdAt: number;
};

type Columns = {
  draft: TaleLite[];
  images: TaleLite[];
  packaged: TaleLite[];
  bound: TaleLite[];
  scheduled: TaleLite[];
  published: TaleLite[];
  failed: TaleLite[];
};

const COLS: Array<keyof Columns> = ['draft','images','packaged','bound','scheduled','published','failed'];

const STATUS_META: Record<keyof Columns, { label: string; hint: string }> = {
  draft: { label: "Draft", hint: "New tales" },
  images: { label: "Images", hint: "Generating art" },
  packaged: { label: "Packaged", hint: "Storyboard/audio ready" },
  bound: { label: "Bound", hint: "Linked to YouTube" },
  scheduled: { label: "Scheduled", hint: "Publish date set" },
  published: { label: "Published", hint: "Live" },
  failed: { label: "Failed", hint: "Needs attention" },
};

export function DraggableKanbanBoard({ initial }: { initial: Columns }) {
  const [cols, setCols] = useState<Columns>(initial);
  const [activeId, setActiveId] = useState<string | null>(null);
  const toastApi = useToastApi();

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } })
  );

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    for (const col of COLS) {
      const item = cols[col].find(x => x.id === activeId);
      if (item) return item;
    }
    return null;
  }, [activeId, cols]);

  function findContainer(id: string) {
    for (const col of COLS) {
      if (cols[col].some(x => x.id === id)) return col;
    }
    return null;
  }

  async function persistReorder(column: keyof Columns) {
    const ids = cols[column].map(x => x.id);
    try {
      await fetch('/api/tales/order', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ column, ids })
      });
    } catch (e) {
      console.error('Failed to persist order:', e);
    }
  }

  async function persistMove(id: string, to: keyof Columns) {
    // If moving into 'scheduled', ask for a date
    let payload: any = { status: to };
    if (to === 'scheduled') {
      const iso = prompt('Pick publish date/time (YYYY-MM-DDTHH:mm, local):');
      if (iso) {
        try {
          payload.scheduledAt = Math.floor(new Date(iso).getTime()/1000);
        } catch {
          toastApi.fail('Invalid date format');
          return;
        }
      }
    }
    
    try {
      const r = await fetch(`/api/tales/${id}/status`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!r.ok) throw new Error(await r.text());
      toastApi.success('Tale moved successfully');
    } catch (e: any) {
      toastApi.fail(e.message || 'Could not update', 'Failed');
      // hard refresh column data on failure
      window.location.reload();
    }
  }

  function onDragStart(ev: DragStartEvent) {
    setActiveId(String(ev.active.id));
  }

  async function onDragEnd(ev: DragEndEvent) {
    const { active, over } = ev;
    setActiveId(null);
    if (!over) return;

    const fromCol = findContainer(String(active.id));
    const overId = String(over.id);

    // Over could be a card or a column droppable id (we'll set column id = column key)
    const toCol = COLS.includes(overId as any) ? (overId as keyof Columns) : findContainer(overId);
    if (!fromCol || !toCol) return;

    if (fromCol === toCol) {
      // Reorder inside same column
      const fromIndex = cols[fromCol].findIndex(x => x.id === active.id);
      const toIndex   = cols[toCol].findIndex(x => x.id === overId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

      setCols(prev => ({
        ...prev,
        [fromCol]: arrayMove(prev[fromCol], fromIndex, toIndex)
      }));
      await persistReorder(fromCol);
    } else {
      // Move across columns
      const item = cols[fromCol].find(x => x.id === active.id)!;
      setCols(prev => ({
        ...prev,
        [fromCol]: prev[fromCol].filter(x => x.id !== active.id),
        [toCol]: [{ ...item, status: toCol }, ...prev[toCol]],
      }));
      await persistMove(item.id, toCol);
      await persistReorder(toCol);
    }
  }

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="grid grid-cols-7 gap-4 p-4">
        {COLS.map((col) => (
          <div key={col} className="rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold capitalize">{STATUS_META[col].label}</h3>
              <span className="text-xs text-slate-500 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">
                {cols[col].length}
              </span>
            </div>
            <div className="text-xs text-slate-500 mb-3">{STATUS_META[col].hint}</div>

            {/* Column is its own droppable target by giving SortableContext the ids */}
            <SortableContext items={cols[col].map(x => x.id)} strategy={verticalListSortingStrategy}>
              <div 
                id={col} 
                className={`space-y-2 min-h-[120px] p-2 rounded-lg border-2 border-dashed transition-colors ${
                  activeId && findContainer(activeId) !== col 
                    ? 'border-slate-300 dark:border-slate-600' 
                    : 'border-transparent'
                }`}
              >
                {cols[col].length === 0 ? (
                  <div className="text-center text-xs text-slate-400 py-8">
                    {col === 'draft' ? 'Create new tales' : 
                     col === 'images' ? 'Queue images' : 
                     col === 'packaged' ? 'Package tales' : 
                     'Drop tales here'}
                  </div>
                ) : (
                  cols[col].map(card => (
                    <SortableItem id={card.id} key={card.id}>
                      <KanbanCard item={card} />
                    </SortableItem>
                  ))
                )}
              </div>
            </SortableContext>
          </div>
        ))}
      </div>

      <DragOverlay>
        {activeItem ? <KanbanCard item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
